import Amplify, { Auth, Hub } from "aws-amplify";
import { ConsoleLogger } from "@aws-amplify/core";
import { HubPayload } from "@aws-amplify/core/lib/Hub";
import qs from "qs";
import { handleEmailSignIn } from "common/utils/account/handleEmailSignIn";
import { handleFederatedSignIn } from "common/utils/account/handleFederatedSignIn";
import { logStart, logError } from "common/utils/logging/Logger";
import { ShouldRenderApp } from "common/utils/ShouldRenderApp";
import { clearSellerPortalAuth, removeLegacyPortalFlexportCookies } from "common/utils/storage/clearSellerPortalAuth";
import { ensureDeviceKeyCookiesExist } from "common/utils/storage/rememberDevice";
import {
  setCustomStateFailure,
  setLogisticsApiPartnerAuthState,
  setPartnerName,
  setFederatedUserExistsOnCore,
  setUserSignupNotSupported,
} from "common/utils/storage/sessionStorage";
import {
  setIdentityCentralized,
  getSelectedFederatedProvider,
  clearSelectedFederatedProvider,
  clearIdentityCentralized,
} from "common/utils/storage/localStorage";
import { amplifyConfig } from "config/amplifyConfig";
import { FederatedProviders, LoginRoutes } from "config/LoginConfig";
import { storeRedirectTargetInSession } from "config/redirectTarget";
import { ConsentPageLoader } from "routes";
import { loadLogisticsApiClient, loadUserClient } from "routes/chunks";

const logger = new ConsoleLogger("HubActions");

async function userIsAuthenticated(): Promise<ShouldRenderApp> {
  const user = await Auth.currentAuthenticatedUser();

  ensureDeviceKeyCookiesExist(user);

  const selectedProvider = getSelectedFederatedProvider();
  const isFlexportProvider = selectedProvider === FederatedProviders.FLEXPORT;

  if (!isFlexportProvider) {
    try {
      const userClient = await loadUserClient();
      const userSignupDataResponse = await userClient.getUserSignupData(user.attributes.email);
      if (userSignupDataResponse?.data?.organization?.isIdentityCentralized) {
        setIdentityCentralized("true");
        clearSellerPortalAuth();
        await Auth.signOut();
        window.location.pathname = LoginRoutes.LANDING;
        return false;
      }
    } catch (e) {
      // USER_NOT_FOUND means the user is new — continue with normal sign-up or sign In flow in handleFederatedSignIn method
      if (e.code !== "USER_NOT_FOUND") {
        logError({ fn: "userIsAuthenticated#identityCentralizedCheck", e }, e.message);
      }
    }
  }

  if (user && user.username.match("Auth0")) {
    return handleFederatedSignIn(user);
  }

  // In case of email / password sign in, don't see this code getting triggered.
  // For email/password, handleEmailSignIn is called directly from useLoginPage.ts after Auth.signIn() returns, so the redirect happens there
  return handleEmailSignIn(user, "");
}

export function hasGoogleOrFacebookOrShopifyAuthError(): boolean {
  return !window.location.href.includes("?code=");
}

export async function startAmplifyHub(): Promise<ShouldRenderApp> {
  const params = qs.parse(window.location.search.slice(1));

  // If cognito pre-signup lambda fails, we get FEDERATED_USER_EXISTS_ON_CORE error in the URL before redirecting to the login page
  // So, we need to set the session storage flag to show the appropriate error message
  if (params?.error_description?.includes("FEDERATED_USER_EXISTS_ON_CORE")) {
    setFederatedUserExistsOnCore("true");
  }

  if (params?.error_description?.includes("USER_SIGNUP_NOT_SUPPORTED")) {
    setUserSignupNotSupported("true");
  }

  storeRedirectTargetInSession(params.redirectUrl);

  if (params.flexportState) {
    setLogisticsApiPartnerAuthState(params.flexportState);
    const logisticsApiClient = await loadLogisticsApiClient();
    const partnerName = await logisticsApiClient.getPartnerName(params.flexportState);
    setPartnerName(partnerName);
    ConsentPageLoader.preload();
  }

  removeLegacyPortalFlexportCookies();
  Amplify.configure(amplifyConfig);

  try {
    const user = await Auth.currentAuthenticatedUser();
    if (user && !window.location.pathname.match("logout")) {
      await Auth.currentSession();
      return userIsAuthenticated();
    }
  } catch {
    // Not authenticated — fall through to login flow
  }

  Hub.listen("auth", (capsule) => {
    logger.info(capsule.payload.event);

    switch (capsule.payload.event) {
      case "signOut":
        onSignOut(capsule.payload);
        break;
      case "customState_failure":
        onCustomStateFailure(capsule.payload);
        break;
    }
  });

  return hasGoogleOrFacebookOrShopifyAuthError();
}

function onSignOut(payload: HubPayload) {
  logStart({ fn: "onSignOut", payload });

  clearSellerPortalAuth();
  clearSelectedFederatedProvider();
  clearIdentityCentralized();
}

function onCustomStateFailure(payload: HubPayload) {
  logError({ fn: "customState_failure" }, payload.event as any);
  setCustomStateFailure("true");
}
